import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../lib/utils";

const buttonVariants = cva(
	"inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:bg-slate-300 disabled:text-slate-50 dark:ring-offset-slate-950 dark:focus-visible:ring-slate-300",
	{
		variants: {
			variant: {
				default: "bg-alta-light-green text-slate-50 hover:bg-alta-dark-green",
				destructive:
					"bg-red-500 text-slate-50 hover:bg-red-500/90 dark:bg-red-900 dark:text-slate-50 dark:hover:bg-red-900/90",
				outline:
					"border border-slate-200 bg-white hover:bg-slate-100 hover:text-slate-900",
				secondary: "bg-slate-100 text-slate-900 hover:bg-slate-100/80",
				ghost: "hover:bg-gray-100 hover:text-gray-900 ",
				link: "text-slate-900 underline-offset-4 hover:underline dark:text-slate-50",
				grayscale: "bg-gray-300 text-white",
			},
			size: {
				default: "h-10 px-4 py-2",
				sm: "h-8 rounded-md px-2 py-1",
				lg: "h-11 rounded-md px-8",
				xs: "h-6 rounded px-2 py-1",
				icon: "h-10 w-10",
			},
		},
		defaultVariants: {
			variant: "default",
			size: "default",
		},
	},
);

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	({ className, variant, size, asChild = false, ...props }, ref) => {
		const Comp = asChild ? Slot : "button";
		return (
			<Comp
				className={cn(buttonVariants({ variant, size, className }))}
				ref={ref}
				{...props}
			/>
		);
	},
);
Button.displayName = "Button";

export { Button, buttonVariants };
